import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { FarewellComponent } from './service-pages/farewell/farewell.component';
import { MaintenanceComponent } from './service-pages/maintenance/maintenance.component';
import { PageNotFoundComponent } from './service-pages/page-not-found/page-not-found.component';
import { CanDeactivateGuard } from './shared/services/can_deactive.service';
import {
  AuthGuardService,
  CompanySectionGuard,
  MentorOrCoachSectionGuard,
  MentorSectionGuard,
  PlatformGuard,
} from './shared/services/auth-guard.service';
import { IframeRendererComponent } from './iframe-renderer/iframe-renderer.component';
import { ReloadComponent } from './service-pages/reload/reload/reload.component';
import { EventNetworkingPublicComponent } from './event-networking/event-networking-public.component';
import { EVENT_NETWORKING } from './shared/utils/event-networking-url';
import { MemberDatabasePublicComponent } from './member-database-public/member-database-public.component';
import { MEMBER_DATABASE } from './shared/utils/member-database-public-url';

export { MEMBER_DATABASE, isMemberDatabasePublicUrl } from './shared/utils/member-database-public-url';

export { EVENT_NETWORKING, isEventNetworkingPublicUrl } from './shared/utils/event-networking-url';

export const LOGINURL = 'auth';
export const DL_SIGNUP_URL = 'auth/dlsignup';
export const PASSWORD_RESET_URL = 'auth/reset/confirm';

export const CAREERPLANNER = 'career-planner';
export const LEADAUTH = 'users';

export const EMBEDGENERATOR = 'embed-generator';
export const INNERSMARTGOALSGENERATOR = 'smart-goals';
export const SMARTGOALSGENERATOR = 'smart-goals-generator';
export const TERMS = 'terms';

export const FAREWELL = 'farewell';
export const MAINTENANCE = 'maintenance';
export const NOTFOUNDURL = 'not-found';
export const RELOAD = 'reload';

export const SETTINGS = 'settings';

export const DASHBOARD = 'dashboard';
export const RELATIONS = 'relations';
export const SHARED = 'shared';
export const SHAREDNOTE = SHARED + '/relation/';

export const MENTORDATABASE = 'mentors';
export const PROFILE = 'profile';

export const COMPANY = 'company';
export const REPORTS = 'reports';

export const CLIENTPORTAL = 'client-portal';
export const JOBLISTINGS = 'job-listings';
export const EVENTPARTNERSHIPS = 'event-partnerships';
export const CLIENTSPOTLIGHT = 'client-spotlight';

export const COMMUNITY = 'community';
export const RESOURCES = 'resources';
export const TUTORIAL = 'tutorial';
export const TASKTRACKER = 'task-tracker';
export const WORKSHOPS = 'workshops';
export const KICKOFF = 'kickoff';
export const TRAINING = 'mentor-training';
export const NETWORKING = 'networking';
export const MENTORRATING = 'mentor-rating';

export const routes: Routes = [
  { path: '', redirectTo: LEADAUTH, pathMatch: 'full' },
  {
    path: MEMBER_DATABASE,
    component: MemberDatabasePublicComponent,
  },
  {
    path: LOGINURL,
    loadChildren: () => import('./authentication/authentication.module').then(m => m.AuthenticationModule),
  },
  {
    path: LEADAUTH,
    loadChildren: () => import('./authentication-lead/authentication-lead.module').then(m => m.AuthenticationLeadModule),
  },
  {
    path: CAREERPLANNER,
    loadChildren: () => import('./career-planner/career-planner.module').then(m => m.CareerPlannerModule),
    canActivate: [AuthGuardService],
  },
  {
    path: NOTFOUNDURL,
    component: PageNotFoundComponent,
  },
  {
    path: FAREWELL,
    component: FarewellComponent,
  },
  {
    path: MAINTENANCE,
    component: MaintenanceComponent,
  },
  {
    path: RELOAD,
    component: ReloadComponent,
  },
  {
    path: TERMS,
    loadChildren: () => import('./service-pages/platform-terms/platform-terms.module').then(m => m.PlatformTermsModule),
  },
  {
    path: EMBEDGENERATOR,
    loadChildren: () => import('./smart-goals-generator/embed-generator.module').then(m => m.EmbedGeneratorModule),
  },
  {
    path: INNERSMARTGOALSGENERATOR,
    loadChildren: () => import('./smart-goals-generator/inner-smart-goals-generator.module').then(m => m.InnerSmartGoalsGeneratorModule),
    canActivate: [AuthGuardService],
  },
  {
    path: SMARTGOALSGENERATOR,
    loadChildren: () => import('./smart-goals-generator/smart-goals-generator.module').then(m => m.SmartGoalsGeneratorModule),
  },
  {
    path: KICKOFF,
    loadChildren: () => import('./kickoff/kickoff.module').then(m => m.KickoffModule),
    canActivate: [AuthGuardService],
  },
  {
    path: TRAINING,
    loadChildren: () => import('./training/training.module').then(m => m.TrainingModule),
    canActivate: [AuthGuardService],
  },
  {
    path: DASHBOARD,
    loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule),
    canActivate: [PlatformGuard],
  },
  {
    path: RELATIONS,
    loadChildren: () => import('./relation/relation.module').then(m => m.RelationModule),
    canActivate: [PlatformGuard],
  },
  {
    path: PROFILE,
    loadChildren: () => import('./profile/profile.module').then(m => m.ProfileModule),
    canActivate: [MentorOrCoachSectionGuard],
  },
  {
    path: SETTINGS,
    loadChildren: () => import('./settings/settings.module').then(m => m.SettingsModule),
    canActivate: [AuthGuardService],
  },
  {
    path: MENTORDATABASE,
    loadChildren: () => import('./mentor-database/mentor-database.module').then(m => m.MentorDatabaseModule),
    canActivate: [MentorSectionGuard],
  },
  {
    path: REPORTS,
    loadChildren: () => import('./reports/reports.module').then(m => m.ReportsModule),
    canActivate: [CompanySectionGuard],
  },
  {
    path: COMPANY,
    loadChildren: () => import('./company-dashboard/company-dashboard.module').then(m => m.CompanyDashboardModule),
    canActivate: [CompanySectionGuard],
  },
  {
    path: SHARED,
    loadChildren: () => import('./shared-space/shared-space.module').then(m => m.SharedSpaceModule),
    canActivate: [PlatformGuard],
  },
  {
    path: TASKTRACKER,
    loadChildren: () => import('./mentee-task-tracker/mentee-task-tracker.module').then(m => m.MenteeTaskTrackerModule),
    canActivate: [PlatformGuard],
  },
  {
    path: WORKSHOPS,
    loadChildren: () => import('./workshops/workshops.module').then(m => m.WorkshopsModule),
    canActivate: [PlatformGuard],
  },
  {
    path: NETWORKING,
    loadChildren: () => import('./networking/networking.module').then(m => m.NetworkingModule),
    canActivate: [PlatformGuard],
  },
  {
    path: EVENT_NETWORKING,
    pathMatch: 'full',
    redirectTo: '/not-found',
  },
  {
    path: `${EVENT_NETWORKING}/:eventId`,
    component: EventNetworkingPublicComponent,
  },

  {
    path: MENTORRATING,
    loadChildren: () => import('./mentor-rating/mentor-rating.module').then(m => m.MentorRatingModule),
    canActivate: [MentorSectionGuard],
  },
  {
    path: ':iframeSrc',
    component: IframeRendererComponent,
    canActivate: [AuthGuardService],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [AuthGuardService, PlatformGuard, MentorSectionGuard, MentorOrCoachSectionGuard, CompanySectionGuard, CanDeactivateGuard],
})
export class AppRoutingModule {}
